import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { useEffect, useMemo, useState } from "react";
import { Flame, X } from "lucide-react";
import { trackView } from "@/lib/api";

export default function ProductDialog({ item, onOpenChange, categoryName }) {
  const { lang, t, isRtl } = useLang();
  const open = !!item;

  const sizes = useMemo(() => (Array.isArray(item?.sizes) ? item.sizes : []), [item]);
  const milks = useMemo(() => (Array.isArray(item?.milk_options) ? item.milk_options : []), [item]);

  const [sizeId, setSizeId] = useState(null);
  const [milkId, setMilkId] = useState(null);

  useEffect(() => {
    if (item) {
      trackView(item.id);
      setSizeId(sizes.length ? sizes[0].id : null);
      setMilkId(milks.length ? milks[0].id : null);
    }
  }, [item, sizes, milks]);

  const selSize = sizes.find((s) => s.id === sizeId) || null;
  const selMilk = milks.find((m) => m.id === milkId) || null;

  // Base comes from the selected size (if any), else the item's own values.
  const base = selSize
    ? selSize
    : { price: item?.price || 0, calories: item?.calories || 0, protein_g: item?.protein_g || 0, carbs_g: item?.carbs_g || 0, fat_g: item?.fat_g || 0 };
  const milkAdd = selMilk || { price: 0, calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };

  const round = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const price = round(base.price + milkAdd.price);
  const calories = round(base.calories + milkAdd.calories);
  const protein_g = round(base.protein_g + milkAdd.protein_g);
  const carbs_g = round(base.carbs_g + milkAdd.carbs_g);
  const fat_g = round(base.fat_g + milkAdd.fat_g);

  const label = (v) => (lang === "ar" ? v.name_ar || v.name_en : v.name_en || v.name_ar);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        dir={isRtl ? "rtl" : "ltr"}
        className="max-w-2xl p-0 overflow-hidden bg-[#FDFBF7] border-0 rounded-3xl max-h-[92vh] overflow-y-auto [&>button]:hidden"
        data-testid="product-dialog"
      >
        {item && (
          <>
            <div className="relative aspect-[16/10] w-full bg-[#F4F2EB]">
              <img
                src={item.image_url}
                alt={lang === "ar" ? item.name_ar : item.name_en}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => onOpenChange(false)}
                data-testid="dialog-close-btn"
                className={`absolute top-4 ${isRtl ? "left-4" : "right-4"} rounded-full bg-white/90 hover:bg-white p-2 transition-colors`}
                aria-label={t.close}
              >
                <X className="w-4 h-4 text-[#1A1A1A]" />
              </button>
            </div>

            <div className="p-8 lg:p-10">
              <p className="text-xs uppercase tracking-[0.3em] text-[#5C6B6D]">
                {categoryName ? categoryName(item.category) : item.category}
              </p>
              <DialogTitle asChild>
                <h2
                  className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} mt-3 text-3xl lg:text-4xl text-[#0F5F63] font-semibold leading-tight`}
                  data-testid="dialog-title"
                >
                  {lang === "ar" ? item.name_ar : item.name_en}
                </h2>
              </DialogTitle>
              <DialogDescription asChild>
                <p className="mt-4 text-[#5C6B6D] leading-relaxed">
                  {lang === "ar" ? item.description_ar : item.description_en}
                </p>
              </DialogDescription>

              {/* Size selector */}
              {sizes.length > 0 && (
                <div className="mt-8" data-testid="size-selector">
                  <p className="text-xs uppercase tracking-widest text-[#5C6B6D] mb-3">{t.chooseSize}</p>
                  <div className="flex flex-wrap gap-2">
                    {sizes.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSizeId(s.id)}
                        data-testid={`size-opt-${s.id}`}
                        className={`rounded-full px-4 py-2 text-sm transition-colors ${
                          sizeId === s.id
                            ? "bg-[#0F5F63] text-[#FDFBF7]"
                            : "border border-[#0F5F63]/20 text-[#0F5F63] hover:border-[#0F5F63]"
                        }`}
                      >
                        {label(s)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Milk selector */}
              {milks.length > 0 && (
                <div className="mt-6" data-testid="milk-selector">
                  <p className="text-xs uppercase tracking-widest text-[#5C6B6D] mb-3">{t.chooseMilk}</p>
                  <div className="flex flex-wrap gap-2">
                    {milks.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setMilkId(m.id)}
                        data-testid={`milk-opt-${m.id}`}
                        className={`rounded-full px-4 py-2 text-sm transition-colors ${
                          milkId === m.id
                            ? "bg-[#0F5F63] text-[#FDFBF7]"
                            : "border border-[#0F5F63]/20 text-[#0F5F63] hover:border-[#0F5F63]"
                        }`}
                      >
                        {label(m)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#5C6B6D]">{t.price}</p>
                  <p className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-3xl text-[#1A1A1A] font-semibold mt-1`} data-testid="dialog-price">
                    {price} <span className="text-lg text-[#5C6B6D]">{t.currency}</span>
                  </p>
                </div>

                <div
                  data-testid="calories-badge"
                  className="inline-flex items-center gap-3 rounded-full bg-[#E27B58] text-white px-5 py-3 shadow-[0_10px_30px_-10px_rgba(226,123,88,0.6)]"
                >
                  <Flame className="w-5 h-5" />
                  <div className="leading-none">
                    <p className="text-[10px] uppercase tracking-widest opacity-90">{t.calories}</p>
                    <p className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-2xl font-semibold mt-1`} data-testid="dialog-calories">
                      {calories} <span className="text-xs font-normal opacity-90">{t.kcal}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Nutrition */}
              {(protein_g > 0 || carbs_g > 0 || fat_g > 0) && (
                <div className="mt-8 pt-6 border-t border-black/5" data-testid="nutrition-block">
                  <p className="text-xs uppercase tracking-[0.3em] text-[#5C6B6D] mb-4">{t.nutrition}</p>
                  <div className="grid grid-cols-3 gap-3">
                    <NutritionCell label={t.protein} value={protein_g} unit={t.grams} />
                    <NutritionCell label={t.carbs} value={carbs_g} unit={t.grams} />
                    <NutritionCell label={t.fat} value={fat_g} unit={t.grams} />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NutritionCell({ label, value, unit }) {
  return (
    <div className="rounded-2xl bg-[#F4F2EB] p-4 text-center">
      <p className="text-[10px] uppercase tracking-widest text-[#5C6B6D]">{label}</p>
      <p className="mt-2 text-2xl font-serif-en text-[#0F5F63] font-semibold">
        {value}
        <span className="text-xs text-[#5C6B6D] font-sans font-normal ms-1">{unit}</span>
      </p>
    </div>
  );
}
